import { useEffect, useRef, useState } from "react";
import { Button } from "../../components/Button";
import { restoreGetStarted } from "../../services/onboarding";
import { useGetStartedChecklist } from "./useGetStartedChecklist";
// Imported here rather than relied on from elsewhere: this card wears .data-table-card, whose rules
// live in DataTable.css. It renders correctly today only because UsersSection happens to import
// that file, so the card would arrive unstyled the day it is mounted on any other page.
import "../../components/DataTable.css";
import "./GetStartedPanel.css";

/**
 * The way back to a dismissed Get Started checklist.
 *
 * The sidebar's Get Started dialog offers the same restore to every user. This card is the admin's
 * copy of it, and it lives in organization settings because that is where the other
 * "how this organization behaves" switches are.
 *
 * It states the current state plainly rather than offering a control that would do nothing, since
 * "show it again" on an already-visible checklist is a button that appears broken.
 */
export const GetStartedVisibilityCard = () => {
  // The one caller that opts in. This card reports what is left to do for a user who has *hidden*
  // the panel, which is precisely the state the server skips the completion queries in unless it is
  // asked. Without the opt-in the three states below collapse to two: `items` would always be
  // absent here, so a finished program would read as "you hid the checklist" and be offered a
  // restore that brings back a panel which immediately hides itself again.
  const { checklist, setChecklist, activeRef } = useGetStartedChecklist({ includeItems: true });
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState(false);
  const [justRestored, setJustRestored] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);

  // A successful restore removes the button that was holding focus, which would drop a keyboard
  // user at the top of the document. Focus moves to this card's heading, so they stay where they
  // were. The heading rather than the status line below: that line is a live region and already
  // announces its own change, and focusing it would read the same sentence twice.
  useEffect(() => {
    if (justRestored) headingRef.current?.focus();
  }, [justRestored]);

  const handleRestore = async () => {
    // aria-disabled leaves the handler attached, so this guard is what stops the second request.
    if (restoring) return;
    setRestoring(true);
    setRestoreError(false);
    try {
      const updated = await restoreGetStarted();
      setChecklist(updated);
      if (activeRef.current && !updated?.dismissedAt) setJustRestored(true);
    } catch {
      if (activeRef.current) setRestoreError(true);
    } finally {
      if (activeRef.current) setRestoring(false);
    }
  };

  // Nothing to say on an older API, or when the read failed.
  if (!checklist) return null;

  const isDismissed = Boolean(checklist.dismissedAt);

  /*
    Three states, not two, and `?? []` collapsed the first into the wrong one.

    An absent `items` means the server did not say — an older API, or one that answered without the
    field — which is not the same as there being nothing left to do. Defaulting it to an empty array
    produced "You have finished every step" as the answer to a question nobody had answered,
    and that sentence also hides the restore button below. The panel had no nav entry then, so a coach
    who had dismissed it was told they were finished and left with no way back at all.

    `items: []` is a real answer and does mean finished: the server sends only the tasks this caller
    can actually complete, so an empty list is a caller with nothing left. That needs no deploy skew
    to reach. Only that middle case may claim completion.

    GetStartedPanel.tsx gets the same payload right at its own gate, and has a test for it.
  */
  const remaining = checklist.items
    ? checklist.items.some((item) => !item.done)
    : null;
  const finished = remaining === false;

  return (
    <section className="get-started-visibility data-table-card" data-testid="get-started-visibility">
      <header className="data-table-card__header">
        <div>
          <h2 className="data-table-card__title" tabIndex={-1} ref={headingRef}>
            Get Started checklist
          </h2>
          <p className="data-table-card__subtitle">
            The short list of setup steps that shows above your players. Hiding it affects your own
            account only, not the rest of your staff.
          </p>
        </div>
      </header>
      <div className="get-started-visibility__body">
        <div>
          {/*
            A live region, because restoring the checklist changes this line and removes the button
            beside it. It is mounted before its text ever changes, which is what makes the
            announcement reliable here but not on the panel's own dismissal notice.
          */}
          <p
            className="get-started-visibility__status"
            role="status"
            data-testid="get-started-visibility-status"
          >
            {/*
              A finished checklist is reported first, even to someone who also dismissed it.
              "You hid the checklist" beside no button reads as a broken control; the real reason
              there is nothing to restore is that there is nothing left to do.
            */}
            {finished
              ? "You have finished every step, so the checklist no longer shows."
              : isDismissed
                ? "You hid the checklist."
                : "The checklist is showing on your players page."}
          </p>
          {restoreError && (
            <p className="get-started-visibility__error" role="alert">
              We could not bring the checklist back just now. Try again in a moment.
            </p>
          )}
        </div>
        {/*
          Offered whenever it would change something, which includes the case where we were not told
          what is left: restoring is harmless and is the only route back, so an unknown must not
          withhold it. Only a checklist we know to be finished hides it, because restoring that
          brings back a panel that immediately hides itself again.

          Not `loading`: the primitive's loading state swaps the label for a hardcoded "Loading...",
          which is both generic and wrong about what is happening. And `aria-disabled` rather than
          `disabled`, because a disabled button leaves the tab order the instant it is pressed,
          dropping focus to <body> on the failure path below. The guard in the handler gives the
          same protection against a second request.
        */}
        {isDismissed && !finished && (
          <Button
            variant="secondary"
            onClick={handleRestore}
            aria-disabled={restoring}
            aria-busy={restoring}
            data-testid="get-started-visibility-restore"
          >
            {restoring ? "Bringing it back…" : "Show it again"}
          </Button>
        )}
      </div>
    </section>
  );
};
